import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Alert, Chip, Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme,
} from '@mui/material';
import { TrendingUp as UpIcon, TrendingDown as DownIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { SectionCard } from './SectionCard';
import { SectionSkeleton } from './SectionSkeleton';
import { getPlatformColor, getPlatformName } from '../config/platforms';

const API_URL = process.env.REACT_APP_API_URL || '';

interface DayPost { id: string; excerpt: string; }
interface SeriesPoint { date: string; followers: number | null; delta: number | null; posts: DayPost[]; }
interface HistoryAccount {
  platform: string;
  account_ref: string;
  account: string;
  series: SeriesPoint[];
}

/** One day on one account where followers moved AND something was published. */
interface Row {
  date: string;
  platform: string;
  account: string;
  delta: number;
  followers: number | null;
  posts: DayPost[];
}

const formatDay = (d: string) =>
  new Date(d + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

/**
 * Days where follower movement and a published post fall together.
 *
 * This is the question people actually open Stats to ask — "did that post cost us followers" —
 * so it gets its own section rather than living behind an expander on each account card.
 *
 * It is flatly a coincidence table, not an attribution model, and says so. The data here makes
 * the point better than any wording: the same post shows +2 on LinkedIn and -6 on Instagram on
 * the same day, because Instagram was mid-purge and the content had nothing to do with it.
 */
export const PostImpact: React.FC<{ days?: number }> = ({ days = 90 }) => {
  const { token } = useAuth();
  const theme = useTheme();
  // Below this width a six-column table cannot be read without pinching.
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [accounts, setAccounts] = useState<HistoryAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [direction, setDirection] = useState<'all' | 'gains' | 'losses'>('all');

  const loadedOnce = useRef(false);

  const load = useCallback(async () => {
    // See FollowerHistory: fetching before AuthContext has restored the session sends
    // `Bearer null`, which the 401 interceptor treats as a dead session.
    if (!token) return;
    // Spinner only on the very first load. A re-fetch — after hiding an account, say — would
    // otherwise swap a populated panel for a spinner and read as the content disappearing.
    //
    // Read through a ref rather than depending on `accounts`: making it a dependency of this
    // callback recreates it on every fetch, which re-runs the effect below and loops.
    if (!loadedOnce.current) setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/audience/history?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Could not load post impact');
      const data = await res.json();
      setAccounts(data.accounts || []);
      loadedOnce.current = true;
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load post impact');
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  // Re-fetch when an account is hidden or unhidden anywhere on the page. Same server-side
  // list drives every audience panel, so they must not drift apart until a reload.
  useEffect(() => {
    const onHiddenChanged = () => { load(); };
    window.addEventListener('shoutvector:audience-hidden-changed', onHiddenChanged);
    return () => window.removeEventListener('shoutvector:audience-hidden-changed', onHiddenChanged);
  }, [load]);


  const rows = useMemo(() => {
    const out: Row[] = [];
    for (const acc of accounts) {
      for (const pt of acc.series) {
        if (pt.delta === null || pt.delta === 0) continue;
        if (!pt.posts.length) continue;
        out.push({
          date: pt.date,
          platform: acc.platform,
          account: acc.account || acc.account_ref,
          delta: pt.delta,
          followers: pt.followers,
          posts: pt.posts,
        });
      }
    }
    // Newest first, then biggest movement — the days worth looking at surface immediately.
    return out.sort((a, b) =>
      a.date === b.date ? Math.abs(b.delta) - Math.abs(a.delta) : b.date.localeCompare(a.date));
  }, [accounts]);

  const shown = rows.filter(r =>
    direction === 'all' ? true : direction === 'gains' ? r.delta > 0 : r.delta < 0);

  if (loading) return <SectionSkeleton frameless rows={3} />;
  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;

  return (
    <SectionCard
      frameless
      title="Posts And Follower Movement"
      actions={
        <ToggleButtonGroup size="small" exclusive value={direction} onChange={(_, v) => v && setDirection(v)}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="gains">Gains</ToggleButton>
          <ToggleButton value="losses">Losses</ToggleButton>
        </ToggleButtonGroup>
      }
      description={<>
        Days when an account's follower count moved <em>and</em> something was published to that
        network. Same day is not proof of cause — it is where to look first.
      </>}
    >

      {!shown.length ? (
        <Alert severity="info">
          Nothing to show yet: no day in this period has both follower movement and a post on
          the same network. It fills in as you publish.
        </Alert>
      ) : (
        <Card variant="outlined">
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            {/* Scrolls inside its own container rather than pushing the page sideways. */}
            <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: narrow ? 0 : 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Day</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell align="right">Change</TableCell>
                  {!narrow && <TableCell>What was published</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((r, i) => (
                  <TableRow key={`${r.platform}-${r.account}-${r.date}-${i}`} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDay(r.date)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          size="small"
                          label={getPlatformName(r.platform)}
                          sx={{ bgcolor: getPlatformColor(r.platform), color: '#fff', height: 20 }}
                        />
                        <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 90, sm: 160 } }}>
                          {r.account}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        variant="outlined"
                        color={r.delta > 0 ? 'success' : 'error'}
                        icon={r.delta > 0 ? <UpIcon /> : <DownIcon />}
                        label={r.delta > 0 ? `+${r.delta}` : r.delta}
                      />
                    </TableCell>
                    {!narrow && (
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {r.posts.map(p => p.excerpt).join(' · ').slice(0, 110)}
                          {r.posts.length > 1 && ` (${r.posts.length} posts)`}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Alert severity="warning" sx={{ mt: 2 }}>
        <strong>Read this as coincidence, not cause.</strong> Your own data shows why: the same
        post appears against a gain on one network and a loss on another the same day, because
        Instagram was removing fake accounts at the time and the content was irrelevant to it.
        A pattern across several posts means something; one row does not.
      </Alert>
    </SectionCard>
  );
};

export default PostImpact;
